<script lang="ts">
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import { getCountryCallingCode } from 'libphonenumber-js';

    import countries from '../../countries.json';
    import { getFlagEmoji } from '../../helper';
    import translations from '../../translations';

    import type { Language } from '$lib/types/Language';
    import type { CountryCode } from 'libphonenumber-js';

    export let open = false;
    export let choices: CountryCode[] = [];
    export let selected: CountryCode = 'DE';
    export let value: CountryCode | '' = '';
    export let language: Language = 'en';
    export let handleSelect: (code: CountryCode) => void;
    export let autoFocusSearchField = false;
    export let focusColor = 'blue';

    let filterInputElement: HTMLInputElement | undefined;
    let dropdownElement: HTMLDivElement;
    let filteredChoices: CountryCode[] = [];
    let filter = '';
    let focusedIndex: number | undefined;

    const scrollToSelectedCountry = () => {
        if (dropdownElement) {
            const scrollableParent = dropdownElement.parentElement;
            const selectedElement = dropdownElement.getElementsByClassName(
                'selected',
            )[0] as HTMLButtonElement;

            if (scrollableParent && selectedElement) {
                scrollableParent.scrollTop =
                    selectedElement.offsetTop - selectedElement.clientHeight - 20;
            }
        }
    };

    const getLocalisedNames = (option: string) => {
        return countries[option as keyof typeof countries];
    };

    const borderRadius = getBorderRadius('dropdownItem');

    // For initial up/down key press when search is focused
    const handleInputKeydown = (evt: KeyboardEvent) => {
        const { key } = evt;

        if (key === 'ArrowDown' || key === 'ArrowUp') {
            evt.preventDefault();

            // Guard against empty filtered choices
            if (filteredChoices.length === 0) {
                return;
            }

            focusedIndex = focusedIndex ?? filteredChoices.indexOf(selected);

            if (focusedIndex === -1) {
                focusedIndex = 0;
            } else {
                focusedIndex =
                    key === 'ArrowDown'
                        ? (focusedIndex + 1) % filteredChoices.length
                        : (focusedIndex - 1 + filteredChoices.length) % filteredChoices.length;
            }

            const buttonId = filteredChoices[focusedIndex];
            const buttonElement = document.getElementById(buttonId);

            if (buttonElement) {
                // As soon as the buttonElement is focused, this key handler will not be triggered any longer
                // -> handleButtonKeydown will be triggered instead
                buttonElement.focus();
            }
        }
    };

    // For navigating with arrow keys within the dropdown
    const handleButtonKeydown = (evt: KeyboardEvent) => {
        const { key } = evt;

        if (key === 'ArrowDown' || key === 'ArrowUp') {
            evt.preventDefault();

            // Guard against empty filtered choices
            if (filteredChoices.length === 0) {
                return;
            }

            const current = focusedIndex ?? 0;
            focusedIndex =
                key === 'ArrowDown'
                    ? (current + 1) % filteredChoices.length
                    : (current - 1 + filteredChoices.length) % filteredChoices.length;

            const buttonId = filteredChoices[focusedIndex];
            const buttonElement = document.getElementById(buttonId);

            if (buttonElement) {
                buttonElement.focus();
            }
        }
    };

    $: choices = choices.sort((a, b) =>
        getLocalisedNames(a)[language].localeCompare(getLocalisedNames(b)[language]),
    );

    $: {
        if (open) {
            scrollToSelectedCountry();
        } else {
            filter = '';
        }
    }

    $: {
        if (open && filterInputElement && autoFocusSearchField) {
            filterInputElement.focus();
        }
    }

    $: filteredChoices = choices.filter((countryCode) => {
        if (!filter.trim()) {
            return true;
        }

        return Object.values(getLocalisedNames(countryCode)).some(
            (name) => name.toLowerCase().indexOf(filter.toLowerCase()) !== -1,
        );
    });
</script>

<div class="fun-group fun-relative fun-flex fun-items-center">
    <span
        class="fun-absolute fun-left-6 fun-text-gray-400 group-focus-within:fun-text-[var(--focusColor)]"
        style="--focusColor: {focusColor}"
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
        >
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M9 3.5C5.96243 3.5 3.5 5.96243 3.5 9C3.5 12.0376 5.96243 14.5 9 14.5C10.519 14.5 11.893 13.8852 12.8891 12.8891C13.8852 11.893 14.5 10.519 14.5 9C14.5 5.96243 12.0376 3.5 9 3.5ZM2 9C2 5.13401 5.13401 2 9 2C12.866 2 16 5.13401 16 9C16 10.6625 15.4197 12.1906 14.4517 13.3911L17.7803 16.7197C18.0732 17.0126 18.0732 17.4874 17.7803 17.7803C17.4874 18.0732 17.0126 18.0732 16.7197 17.7803L13.3911 14.4517C12.1906 15.4197 10.6625 16 9 16C5.13401 16 2 12.866 2 9Z"
                fill="currentColor"
            />
        </svg>
    </span>
    <input
        class="fun-w-full fun-border-b fun-py-4 fun-pl-14 fun-placeholder-gray-500 fun-outline-none fun-ring-0"
        placeholder={language === 'de' ? 'Suche' : 'Search'}
        type="text"
        bind:value={filter}
        bind:this={filterInputElement}
        on:keydown={handleInputKeydown}
    />
</div>

<div
    class="fun-my-3x scroll-p-2 fun-pointer-events-auto fun-max-h-96 fun-w-full fun-overflow-y-auto"
    on:click|stopPropagation
    on:keydown
    role="button"
    tabindex={open ? -1 : 0}
>
    {#if !!filteredChoices && filteredChoices.length > 0}
        <div class="fun-grid fun-gap-1 fun-p-2" bind:this={dropdownElement}>
            {#each filteredChoices as option (option)}
                {@const optionNames = getLocalisedNames(option)}
                {@const isSelected = selected === option || value === option}

                <button
                    id={option}
                    on:click|preventDefault={() => open && handleSelect(option)}
                    on:keydown={handleButtonKeydown}
                    tabindex={open ? 0 : -1}
                    class={cn(
                        'fun-flex fun-w-full fun-min-w-0 fun-items-center fun-gap-2 fun-px-3 fun-py-2 fun-text-sm fun-font-normal fun-outline-none fun-transition-none sm:hover:fun-bg-gray-100 sm:focus:fun-bg-gray-100 md:fun-text-base',
                        borderRadius,
                        {
                            'fun-border-blue-500 fun-bg-blue-50 fun-text-blue-600': isSelected,
                            'fun-border-gray-200 fun-text-gray-500': !isSelected,
                        },
                    )}
                >
                    <span class="fun-flex-shrink-0 fun-text-2xl">{getFlagEmoji(option)}</span>
                    <span class="fun-truncate">
                        <span>{optionNames[language]}</span>
                        {#if optionNames[language] !== optionNames.native}
                            <span>({getLocalisedNames(option).native})</span>
                        {/if}
                    </span>
                    <span
                        class={cn('fun-ml-auto fun-flex-shrink-0 fun-text-gray-500', {
                            'fun-text-blue-600': isSelected,
                        })}
                    >
                        +{getCountryCallingCode(option)}
                    </span>
                </button>
            {/each}
        </div>
    {:else}
        <p class="fun-p-5 fun-text-sm fun-text-gray-500">
            {translations[language].noCountryFound}
        </p>
    {/if}
</div>
